/* Shared email-send helpers — confidentiality disclosure append logic
   for every outbound flow in the app (GFI/Ground-Fresh AND SweetLeaf).

   History: appendSlDisclosure started as a SweetLeafPortal.js-only
   helper with a hardcoded string. It moved here once Ground-Fresh
   needed its OWN editable disclosure (CompanyProfile.js's new
   confidentialityDisclosureGf field) plus logic to pick the right one
   based on which email account the user is sending from — SweetLeaf
   is a fully separate settings domain (gfi-sl-settings /
   sweetleaf_settings) from Ground-Fresh's CompanyProfile
   (company_profile), so there's no single "the" disclosure; the
   correct one depends on the send. */

var SL_DEFAULT_DISCLOSURE = "CONFIDENTIALITY NOTICE\nThe information contained in this e-mail is confidential and/or proprietary to Sweetleaf Produce Inc. and/or its affiliates and may only be used solely in performance of work or services Sweetleaf Produce Inc. The information transmitted herewith is intended only for use by the individual or entity to which it is addressed. If the reader of this message is not the intended recipient, you are hereby notified that any review, retransmission, dissemination, distribution, copying or other use of, or taking of any action in reliance upon this information is strictly prohibited. If you have received this communication in error, please contact the sender and delete the material from your computer.";

var GF_DEFAULT_DISCLOSURE = "CONFIDENTIALITY NOTICE\nThe information contained in this e-mail is confidential and/or proprietary to Ground-fresh International Inc. and/or its affiliates and may only be used solely in performance of work or services for Ground-Fresh International Inc. The information transmitted herewith is intended only for use by the individual or entity to which it is addressed. If the reader of this message is not the intended recipient, you are hereby notified that any review, retransmission, dissemination, distribution, copying or other use of, or taking of any action in reliance upon this information is strictly prohibited. If you have received this communication in error, please contact the sender and delete the material from your computer.";

function escapeDisclosureHtml(s) {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/* Text is user-edited free text (Settings/Company Profile textarea),
   never trusted markup like a signature — always escaped before it
   lands in an outgoing HTML email. white-space:pre-line preserves the
   textarea's own line breaks without needing <br> conversion. */
function wrapDisclosure(text) {
  return '<div style="color: #999; font-size: 10px; font-style: italic; margin-top: 20px; border-top: 1px solid #ddd; padding-top: 10px; white-space: pre-line;">' + escapeDisclosureHtml(text) + '</div>';
}

/* Protection Notice box (2026-09-14, Arturo) — used by the new "Email
   Shipper — Protection" flow (InvoiceRegistry.js) so the shipper sees
   Ground-Fresh's editable protectionNoticeEs/En text (CompanyProfile.js
   — the SAME field PaymentRegistry.js's Payment Confirmation PDF
   already reads, see paymentConfirmation.js) inline in the email body.
   Originally appended below the signature; Arturo corrected this the
   same day ("es algo importante que deben leer... debe ser en la
   primer linea, antes de estimados") — it's now PREPENDED so it's the
   very first thing the shipper sees, before the "Estimados," greeting.
   More prominent than wrapDisclosure's tiny fine print on purpose —
   this is operational content the shipper needs to actually read, not
   boilerplate. */
function wrapProtectionNotice(text) {
  /* 2026-09-15 (Arturo): companyProfile.protectionNoticeEs/En already
     starts with "AVISO DE PROTECCIÓN:" on its own — a hardcoded
     heading here duplicated the phrase. Removed; the configured text
     is shown as-is. */
  return '<div style="margin-bottom: 18px; padding: 12px 14px; background: #FFF8E1; border: 1px solid #F5C244; border-radius: 6px; font-size: 12px; color: #6b4e00; white-space: pre-line;">' + escapeDisclosureHtml(text) + '</div>';
}
/* Prepends (not appends) the notice so it renders as the first thing
   in the email body, before the greeting/message/signature block that
   `bodyHtml` already contains by the time postProcessHtmlBody runs
   (see EmailDialog.js's handleSend: message + doc embed + signature
   are concatenated into htmlBody BEFORE postProcessHtmlBody is
   called). */
export function appendProtectionNotice(bodyHtml, companyProfile, lang) {
  var isEs = lang === "es";
  var text = isEs
    ? (companyProfile && companyProfile.protectionNoticeEs) || ""
    : (companyProfile && companyProfile.protectionNoticeEn) || "";
  if (!text) return bodyHtml || "";
  return wrapProtectionNotice(text) + (bodyHtml || "");
}

/* Quality Report (Texas QC) disclosure — 2026-09-14, Arturo. Same
   prepend treatment as appendProtectionNotice above (this is content
   the shipper needs to read up front, not fine print) and the same
   box styling for visual consistency between the two shipper-facing
   "important notice" emails. */
function wrapQualityReportDisclosure(text) {
  return '<div style="margin-bottom: 18px; padding: 12px 14px; background: #FFF8E1; border: 1px solid #F5C244; border-radius: 6px; font-size: 12px; color: #6b4e00; white-space: pre-line;"><strong>AVISO — REPORTE DE CALIDAD</strong><br>' + escapeDisclosureHtml(text) + '</div>';
}
export function appendQualityReportDisclosure(bodyHtml, companyProfile, lang) {
  var isEs = lang === "es";
  var text = isEs
    ? (companyProfile && companyProfile.qualityReportDisclosureEs) || ""
    : (companyProfile && companyProfile.qualityReportDisclosureEn) || "";
  if (!text) return bodyHtml || "";
  return wrapQualityReportDisclosure(text) + (bodyHtml || "");
}

/* SweetLeaf-only — always appends slSettings.confidentialityDisclosure
   (editable in SweetLeaf Settings), no account detection needed since
   every SweetLeafPortal.js send is from the SL account by definition. */
export function appendSlDisclosure(bodyHtml, slSettings) {
  var text = (slSettings && slSettings.confidentialityDisclosure) || SL_DEFAULT_DISCLOSURE;
  return (bodyHtml || "") + wrapDisclosure(text);
}

/* Ground-Fresh side — auto-selects between the SL and GF disclosure
   based on which account the email is actually going out from.
   `selectedFromEmail` should be the resolved account identifier
   EmailDialog is sending as (its maskedFrom alias if set, else its
   real email — see EmailDialog.js's postProcessHtmlBody call) so a
   SweetLeaf-named account is reliably detected even though
   company.emailAccounts is a free-form array with no fixed "gf"/"sl"
   keys (there's no companyProfile.emailAccounts.gf.masked path in the
   real data model — every account in that array is just
   { id, name, email, maskedFrom, signature }, user-managed).
   Matching rule: looks like SweetLeaf (name/address contains
   "sweetleaf", or exactly matches slSettings.maskedEmailAccount) ->
   SL disclosure. Anything else, including no match at all -> GF
   disclosure (Ground-Fresh is this app's primary brand, the sane
   default). */
export function appendDisclosureByAccount(bodyHtml, selectedFromEmail, companyProfile, slSettings) {
  var probe = String(selectedFromEmail || "").toLowerCase();
  var slMasked = String((slSettings && slSettings.maskedEmailAccount) || "").toLowerCase();
  var isSl = probe.indexOf("sweetleaf") >= 0 || (!!slMasked && probe === slMasked);
  var text = isSl
    ? ((slSettings && slSettings.confidentialityDisclosure) || SL_DEFAULT_DISCLOSURE)
    : ((companyProfile && companyProfile.confidentialityDisclosureGf) || GF_DEFAULT_DISCLOSURE);
  return (bodyHtml || "") + wrapDisclosure(text);
}

/* Email routing rule (2026-09-14, Arturo): "emails a four seasons y
   agrocap, solo se pueden enviar desde el modulo sweetleaf y de la
   masked account de sweetleaf... todos los emails a shippers,
   warehouse, customs, otros clientes distintos a four seasons, deben
   ser exclusivamente desde el modulo de ground-fresh... si se intenta
   lo contrario no permitir el envio y enviar mensaje de error."
   Enforced at each send's entry point (the kebab-menu action, before
   the dialog even opens) in InvoiceRegistry.js and SweetLeafPortal.js
   — blocks the attempt outright rather than letting the dialog open
   and fail later. Kept here (not duplicated per file) since it's pure
   routing logic, no UI, same category as appendDisclosureByAccount
   above. */
export function isFourSeasonsClientName(name) {
  return String(name || "").toLowerCase().indexOf("four seasons") >= 0;
}

export function fourSeasonsRoutingBlockedMessage(lang) {
  return lang === "es"
    ? "Los emails a Four Seasons/AGROCAP solo se pueden enviar desde el módulo de SweetLeaf, con la cuenta enmascarada de SweetLeaf. Usa el módulo SweetLeaf para este envío."
    : "Emails to Four Seasons/AGROCAP can only be sent from the SweetLeaf module, using the SweetLeaf masked account. Use the SweetLeaf module for this send.";
}

export function nonFourSeasonsRoutingBlockedMessage(lang) {
  return lang === "es"
    ? "Los emails a shippers, bodegas, aduana u otros clientes deben enviarse exclusivamente desde el módulo de Ground-Fresh, con la cuenta enmascarada de Ground-Fresh. Usa el módulo de Invoice/Ground-Fresh para este envío."
    : "Emails to shippers, warehouses, customs, or other clients must be sent exclusively from the Ground-Fresh module, using the Ground-Fresh masked account. Use the Ground-Fresh Invoice module for this send.";
}

/* Shipper-contact email-category preferences (fix-dashboard.md "Fase 2
   + Feature preferencias de email por contacto", scoped to shipper
   flows only). SupplierRegistry.js stores each contact's opt-in list
   as contact.emailPreferences: string[] drawn from
   SHIPPER_EMAIL_CATEGORIES. The field is only ever an explicit array
   once a user has touched the checkboxes in the contact form —
   EMPTY_CONTACT deliberately leaves it undefined for brand-new
   contacts, and existing contacts saved before this feature also have
   it undefined. Both cases must default to "receives everything"
   (fix-dashboard.md: "considerarse como 'all' por default") — so
   `undefined`/non-array means "all categories", while an explicit `[]`
   means the user unchecked every box (deliberate opt-out of
   everything), which is a DIFFERENT state and must NOT also mean
   "all". Only an Array.isArray check (not `.length`) can tell those
   two apart. */
export var SHIPPER_EMAIL_CATEGORIES = ["shipment", "sales_report", "payment_confirmation", "protection", "quality_report", "compliance"];

export function contactReceivesCategory(contact, category) {
  var prefs = contact && contact.emailPreferences;
  if (!Array.isArray(prefs)) return true;
  return prefs.indexOf(category) !== -1;
}

/* Filters a supplier's contacts[] down to those opted into `category`
   (or never-configured, per contactReceivesCategory above), mapped to
   the {name, email} shape EmailDialog/EmailSalesReportModal expect.
   Callers should fall back to supplier.email when this returns []. */
export function filterContactsByCategory(contacts, category) {
  return (Array.isArray(contacts) ? contacts : [])
    .filter(function (c) { return c && c.email && contactReceivesCategory(c, category); });
}
